import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { PageTitleUpdater } from "./components/PageTitleUpdater";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Callback from "./pages/Callback";
import Calendar from "./pages/Calendar";
import Dashboard from "./pages/Dashboard";
import Employee from "./pages/Employee";
import HR from "./pages/HR";
import Employees from "./pages/hr/Employees";
import Teams from "./pages/hr/Teams";
import LeaveQuotas from "./pages/hr/LeaveQuotas";
import Holidays from "./pages/hr/Holidays";
import Report from "./pages/hr/Report";
import Upload from "./pages/hr/Upload";
import Manager from "./pages/Manager";
import NotFound from "./pages/NotFound";
import AccountSettings from "./pages/AccountSettings";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeProvider>
          <AuthProvider>
            <Toaster />
            <BrowserRouter>
              <PageTitleUpdater />
              <Routes>
                {/* Public Routes - No Auth Required */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/callback" element={<Callback />} />

                {/* Protected Routes - Require Authentication */}
                <Route
                  path="/"
                  element={
                    <Navigate to="/dashboard" replace />
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee"
                  element={
                    <ProtectedRoute>
                      <Employee />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manager"
                  element={
                    <ProtectedRoute>
                      <Manager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr"
                  element={
                    <ProtectedRoute>
                      <HR />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <ProtectedRoute>
                      <Teams />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leave-quotas"
                  element={
                    <ProtectedRoute>
                      <LeaveQuotas />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/employees"
                  element={
                    <ProtectedRoute>
                      <Employees />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/holidays"
                  element={
                    <ProtectedRoute>
                      <Holidays />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Report />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/upload"
                  element={
                    <ProtectedRoute>
                      <Upload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <Calendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account-settings"
                  element={
                    <ProtectedRoute>
                      <AccountSettings />
                    </ProtectedRoute>
                  }
                />

                {/* 404 Not Found - No Auth Required */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
